/**
 * ConnectorRequestAnnotation
 *
 * Renders the Losi Code connector card when losiCode__request_connector
 * (or a start_coding_run needsConnector payload) is present. Mirrors the
 * location-request annotation: interactive card → auth → chat auto-continues.
 */

'use client'

import { useContext, useMemo, useState } from 'react'
import { Annotation, Annotation2 } from '@/types/annotation'
import { ConnectorRequestPrompt } from '@/components/chat/connector-request-prompt'
import { ChatbotUIChatContext } from '@/context/chat'
import type { LosiConnectorType } from '@/lib/connectors/types'

// --------------

type ConnectorItem = {
  type: LosiConnectorType
  connected: boolean
  label: string
  reason?: string
}

type ConnectorRequestData = {
  action?: string
  reason?: string
  message?: string
  workspaceId?: string
  spaceId?: string
  taskId?: string
  connectors?: ConnectorItem[]
  missingConnectors?: LosiConnectorType[]
  needsConnector?: boolean
  ok?: boolean
  error?: string
}

const ANNOTATION_KEYS = [
  'losiCode__request_connector',
  'losiCode__check_coding_readiness',
  'losiCode__start_coding_run'
] as const

// --------------

function readPayload(annotation: Annotation | Annotation2): ConnectorRequestData | null {
  const record = annotation as Record<string, unknown>

  for (const key of ANNOTATION_KEYS) {
    const raw = record[key] as Record<string, unknown> | undefined
    if (!raw) continue
    const data = (
      'result' in raw && raw.result && typeof raw.result === 'object'
        ? raw.result
        : raw
    ) as ConnectorRequestData

    if (key === 'losiCode__request_connector' && data.action === 'request_connector') {
      return data
    }

    // Also surface a card when start/readiness reports missing connectors.
    if (
      (data.needsConnector || (data.missingConnectors && data.missingConnectors.length > 0)) &&
      Array.isArray(data.connectors) &&
      data.connectors.some((c) => !c.connected)
    ) {
      return {
        ...data,
        action: 'request_connector',
        reason:
          data.reason ||
          data.error ||
          data.message ||
          'Connect coding connectors to continue.'
      }
    }
  }

  return null
}

// --------------

export const ConnectorRequestAnnotation: React.FC<{
  annotation: Annotation | Annotation2
}> = ({ annotation }) => {
  const [declined, setDeclined] = useState(false)
  const { isGenerating } = useContext(ChatbotUIChatContext)

  const data = useMemo(() => readPayload(annotation), [annotation])

  const connectors = useMemo(() => {
    if (data?.connectors?.length) {
      return data.connectors.filter((c) => !c.connected)
    }
    return (data?.missingConnectors || []).map((type) => ({
      type,
      connected: false,
      label:
        type === 'github_app'
          ? 'GitHub'
          : type === 'kiro_cli'
            ? 'Kiro CLI'
            : type === 'codex_cli'
              ? 'Codex CLI'
              : type === 'claude_code'
                ? 'Claude Code'
                : 'Repository'
    }))
  }, [data])

  if (!data || data.action !== 'request_connector') return null
  if (declined) return null
  if (isGenerating) return null
  if (connectors.length === 0) return null

  return (
    <div className="py-2">
      <ConnectorRequestPrompt
        reason={
          data.reason ||
          data.message ||
          'Connect a coding connector so Losi can run large changes through the CLI.'
        }
        connectors={connectors}
        workspaceId={data.workspaceId}
        spaceId={data.spaceId}
        onDeclined={() => setDeclined(true)}
      />
    </div>
  )
}

export default ConnectorRequestAnnotation
